<template>
    <div class="flex flex-col items-center relative gap bg-(--color-highlight-background)" :style="phaseTypeColorStyle">
        <Tooltip class="min-w-full flex items-center flex-col">
            <template #trigger="tooltipProps">
                <div
                    class="text-gray-700 text-xxs focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-700 focus-visible:z-10 font-light w-full text-center hover:bg-(--color-highlight-background-hover)"
                    v-bind="tooltipProps"
                    :tabindex="0"
                >
                    {{ props.labelShort }}
                </div>
            </template>

            <template #text>{{ props.labelLong }}</template>
        </Tooltip>

        <Tooltip>
            <template #trigger="tooltipProps">
                <div
                    class="min-h-5 w-17 flex items-center justify-center border-white text-sm/none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-700 focus-visible:z-10 font-bold"
                    :class="{
                        'bg-gray-300 text-gray-500': !isPhaseUsed,
                        'bg-(--color-phase-background) text-(--color-phase-text) hover:bg-(--color-phase-background-hover)':
                            isPhaseUsed,
                        'rounded-l-full border-l-1 border-y-1': props.form === 'Left-End',
                        'border-y-1': props.form === 'Middle',
                        'rounded-r-full border-r-1 border-y-1': props.form === 'Right-End',
                    }"
                    v-bind="tooltipProps"
                    :tabindex="0"
                >
                    {{ dayCount }}
                </div>
            </template>

            <template #header>{{ props.labelLong }}</template>

            <template #text>
                <i18n
                    path="phase-duration-count"
                    :args="{ count: dayCount }"
                    :class="{ 'text-gray-600': dayCount === 0 }"
                >
                    <template #dayCount>
                        <span class="font-semibold">
                            {{ dayCount }}
                        </span>
                    </template>
                </i18n>
            </template>
        </Tooltip>

        <div
            class="flex flex-col gap-px w-full h-full bg-(--color-highlight-border) hover:bg-(--color-highlight-border-hover)"
        >
            <template v-if="props.phaseStart && props.phaseEnd">
                <Tooltip class="min-w-full h-full">
                    <template #trigger="tooltipProps">
                        <div
                            class="w-full flex flex-col items-center text-gray-700 font-light text-xs leading-[1.15] grow justify-center py-px gap-px focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-700 focus-visible:z-10 bg-(--color-highlight-background) hover:bg-(--color-highlight-background-hover)"
                            v-bind="tooltipProps"
                            :tabindex="0"
                        >
                            <template v-if="dayCount > 1">
                                <div class="tabular-nums font-medium">
                                    {{ startDate }}
                                </div>

                                <span class="font-[0.3rem] leading-[0.1em] pb-px text-gray-400">-</span>

                                <div class="tabular-nums font-medium">
                                    {{ endDate }}
                                </div>
                            </template>

                            <template v-else-if="dayCount === 1">
                                <div class="tabular-nums font-medium">
                                    {{ startDate }}
                                </div>
                            </template>
                        </div>
                    </template>

                    <template #header>{{ props.labelLong }}</template>

                    <template #text>
                        <template v-if="dayCount > 1">
                            {{ $t('phase-start-from-to-end', { from: startDateFull, to: endDateFull }) }}
                        </template>
                        <template v-else-if="dayCount === 1">
                            {{ $t('phase-single-day', { day: startDateFull }) }}
                        </template>
                    </template>
                </Tooltip>

                <Tooltip class="min-w-full flex flex-col text-center">
                    <template #trigger="tooltipProps">
                        <div
                            class="text-gray-700 font-light text-xs leading-[1.15] py-0.25 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-700 focus-visible:z-10 bg-(--color-highlight-background) hover:bg-(--color-highlight-background-hover)"
                            v-bind="tooltipProps"
                            :tabindex="0"
                        >
                            <div v-if="dayCount === 1">{{ weekDayStartShort }}</div>
                            <div v-if="dayCount > 1">{{ weekDayStartShort }} - {{ weekDayEndShort }}</div>
                        </div>
                    </template>

                    <template #header>{{ props.labelLong }}</template>

                    <template #text>
                        <div v-if="dayCount === 1">
                            {{ $t('phase-single-day', { day: weekDayStartLong }) }}
                        </div>
                        <div v-else-if="dayCount > 1">
                            {{ $t('phase-start-from-to-end', { from: weekDayStartLong, to: weekDayEndLong }) }}
                        </div>
                    </template>
                </Tooltip>

                <Tooltip
                    class="min-w-full flex items-center flex-col text-gray-700 font-light text-xs leading-[1.15] tabular-nums py-0.25 bg-(--color-highlight-background) hover:bg-(--color-highlight-background-hover)"
                >
                    <template #trigger="tooltipProps">
                        <div
                            v-if="calendarWeekStart === calendarWeekEnd"
                            class="focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-700"
                            v-bind="tooltipProps"
                            :tabindex="0"
                        >
                            {{
                                $t('calendar-week', {
                                    calendarWeek: calendarWeekStart,
                                })
                            }}
                        </div>
                        <div
                            v-else
                            class="focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-700"
                            v-bind="tooltipProps"
                            :tabindex="0"
                        >
                            {{
                                $t('calendar-week-from-to', {
                                    calendarWeekFrom: calendarWeekStart,
                                    calendarWeekTo: calendarWeekEnd,
                                })
                            }}
                        </div>
                    </template>

                    <template #header>{{ props.labelLong }}</template>

                    <template #text>
                        <div v-if="calendarWeekStart === calendarWeekEnd">
                            <i18n path="calendar-week-full">
                                <template #calendarWeek>
                                    <span class="font-semibold">
                                        {{ calendarWeekStart }}
                                    </span>
                                </template>
                            </i18n>
                        </div>

                        <div v-else>
                            <i18n path="calendar-week-full-from-to">
                                <template #calendarWeekFrom>
                                    <span class="font-semibold">
                                        {{ calendarWeekStart }}
                                    </span>
                                </template>

                                <template #calendarWeekTo>
                                    <span class="font-semibold">
                                        {{ calendarWeekEnd }}
                                    </span>
                                </template>
                            </i18n>
                        </div>
                    </template>
                </Tooltip>
            </template>

            <template v-else>
                <Tooltip class="min-w-full h-full flex flex-col text-center">
                    <template #trigger="tooltipProps">
                        <div
                            class="flex flex-col items-center justify-center w-full h-full bg-gray-100 text-gray-400 font-light text-xs focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-700 focus-visible:z-10 hover:bg-(--color-highlight-background-hover)"
                            v-bind="tooltipProps"
                            :tabindex="0"
                        >
                            <div>-</div>
                        </div>
                    </template>

                    <template #header>{{ props.labelLong }}</template>

                    <template #text>
                        <span class="text-gray-600">
                            {{ $t('phase-not-used') }}
                        </span>
                    </template>
                </Tooltip>
            </template>
        </div>
    </div>
</template>

<script setup lang="ts">
import { DateTime } from 'luxon';
import { computed } from 'vue';
import { PhaseColor } from '../../components/Timeline/Timeline.types';
import {
    getAllocationColor100ForPhaseType,
    getAllocationColor200ForPhaseType,
    getAllocationColor300ForPhaseType,
    getBaseColorForPhaseType,
    getPlus100ColorForPhaseType,
    getTextColorForPhaseType,
} from '../Timeline/phaseColors';
import Tooltip from '../Tooltip/Tooltip.vue';

type PhasesTilePhaseProps = {
    labelShort: string;
    labelLong: string;
    isHighlighted: boolean;
    form: 'Left-End' | 'Middle' | 'Right-End';
    phaseStart: DateTime | null;
    phaseEnd: DateTime | null;
    phaseColor: PhaseColor;
};

const props = defineProps<PhasesTilePhaseProps>();

const isPhaseUsed = computed(() => {
    return props.phaseStart !== null && props.phaseEnd !== null;
});

const dayCount = computed(() => {
    if (props.phaseStart && props.phaseEnd) {
        return props.phaseEnd.diff(props.phaseStart, 'days').days;
    }

    return 0;
});

const startDate = computed(() => {
    if (props.phaseStart) {
        return props.phaseStart.toLocaleString({ month: '2-digit', day: '2-digit' });
    }

    return null;
});

const startDateFull = computed(() => {
    if (props.phaseStart) {
        return props.phaseStart.toLocaleString({ month: '2-digit', day: '2-digit', year: 'numeric' });
    }

    return null;
});

const endDate = computed(() => {
    if (props.phaseEnd) {
        return props.phaseEnd.minus({ days: 1 }).toLocaleString({ month: '2-digit', day: '2-digit' });
    }

    return null;
});

const endDateFull = computed(() => {
    if (props.phaseEnd) {
        return props.phaseEnd.toLocaleString({ month: '2-digit', day: '2-digit', year: 'numeric' });
    }

    return null;
});

const weekDayStartShort = computed(() => {
    if (props.phaseStart) {
        return props.phaseStart.weekdayShort;
    }

    return null;
});

const weekDayEndShort = computed(() => {
    if (props.phaseEnd) {
        return props.phaseEnd.minus({ days: 1 }).weekdayShort;
    }

    return null;
});

const weekDayStartLong = computed(() => {
    if (props.phaseStart) {
        return props.phaseStart.weekdayLong;
    }

    return null;
});

const weekDayEndLong = computed(() => {
    if (props.phaseEnd) {
        return props.phaseEnd.minus({ days: 1 }).weekdayLong;
    }

    return null;
});

const calendarWeekStart = computed(() => {
    if (props.phaseStart) {
        return props.phaseStart.weekNumber;
    }

    return null;
});

const calendarWeekEnd = computed(() => {
    if (props.phaseEnd) {
        return props.phaseEnd.minus({ days: 1 }).weekNumber;
    }

    return null;
});

const phaseTypeColorStyle = computed(() => {
    return {
        '--color-phase-background': `var(${getBaseColorForPhaseType(props.phaseColor)})`,
        '--color-phase-background-hover': `var(${getPlus100ColorForPhaseType(props.phaseColor)})`,
        '--color-phase-text': `var(${getTextColorForPhaseType(props.phaseColor)})`,
        '--color-highlight-background': props.isHighlighted
            ? `var(${getAllocationColor100ForPhaseType(props.phaseColor)})`
            : 'var(--color-gray-100)',
        '--color-highlight-border': props.isHighlighted
            ? `var(${getAllocationColor200ForPhaseType(props.phaseColor)})`
            : 'var(--color-gray-200)',
        '--color-highlight-background-hover': props.isHighlighted
            ? `var(${getAllocationColor200ForPhaseType(props.phaseColor)})`
            : 'var(--color-gray-200)',
        '--color-highlight-border-hover': props.isHighlighted
            ? `var(${getAllocationColor300ForPhaseType(props.phaseColor)})`
            : 'var(--color-gray-300)',
    };
});
</script>
